import React, { createContext, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { login } from '../services/auth.service';
import { useLocalStorage } from './use-local-storage.hook';
import { useSnackbars } from './snackbar.hook';
import jwtDecode from 'jwt-decode';
import AuthVerify from './auth-verify.hook';
import { AuthDto } from '../services/dtos/auth.dto';
import { ConnectedUserDto } from '../services/dtos/connected-user-dto';
import { getUsersInfos } from '../services/user.service';

export interface AuthContextType {
    user: ConnectedUserDto | null;
    signin: (data?: AuthDto) => void;
    signout: () => void;
    expired: () => void;
    loggedInAs: (id: string, mail: string) => void;
    signoutLoggedInAs: () => void;
}

export const AuthContext = createContext<AuthContextType>({
    user: null,
    signin: () => {},
    signout: () => {},
    expired: () => {},
    loggedInAs: () => {},
    signoutLoggedInAs: () => {},
});

/**
 * Hook gérant la connexion et déconnexion
 * @param children Element React à afficher
 * @param userData Informations de l'utilisateur
 * @constructor
 */
export const AuthProvider = ({
    children,
    userData,
}: Readonly<{
    children: React.ReactNode;
    userData: ConnectedUserDto | string | null;
}>) => {
    //State plus objects necessaires au provider
    const normalizedUserData =
        typeof userData === 'string' ? (JSON.parse(userData) as ConnectedUserDto | null) ?? null : userData;
    const [user, setUser] = useLocalStorage<ConnectedUserDto | null>('user', normalizedUserData);
    //Navigate
    const navigate = useNavigate();
    //Snackbar
    const showMessage = useSnackbars();

    const value = useMemo(() => {
        const signin = (data?: AuthDto) => {
            // Récupération du token, enregistrement de la connexion et retour à la page d'accueil
            if (data) {
                login(data)
                    .then(token => {
                        let pathTo = '/';
                        if (token) {
                            //On décode le token pour le récupérer sous forme d'objet
                            const userConnected: ConnectedUserDto = jwtDecode(token);
                            userConnected.token = token;
                            setUser(userConnected);
                            if (userConnected.resetPassword) {
                                pathTo = '/changement-mot-de-passe';
                            }
                        }
                        navigate(pathTo, { replace: true });
                    })
                    .catch(() => {
                        //Affichage d'erreur
                        showMessage.errorAlert('Votre login et/ou mot de passe est invalide');
                    });
            }
        };
        const signoutLoggedInAs = () => {
            if (!user) {
                return;
            }

            const updatedUser: ConnectedUserDto = {
                ...user,
                emailLoggedInAS: undefined,
                idLoggedInAS: undefined,
            };
            setUser(updatedUser);
            showMessage.successAlert('Déconnexion réussie');
            navigate('/', { replace: true });
        };
        const loggedInAs = (id: string, emailLoggedInAS: string) => {
            if (!user) {
                return;
            }

            getUsersInfos(id)
                .then(() => {
                    //Récupération des données
                    const updatedUser: ConnectedUserDto = {
                        ...user,
                        idLoggedInAS: id,
                        emailLoggedInAS,
                    };
                    setUser(updatedUser);
                    showMessage.successAlert('Connexion réussie');
                    navigate('/', { replace: true });
                })
                .catch(() => {
                    //Affichage d'erreur
                    showMessage.errorAlert(`Impossible de ce connecter en tant que avec : ${emailLoggedInAS}`);
                });
        };
        const signout = () => {
            // Suppression de la connexion et retour à la page d'accueil
            setUser(null);
            navigate('/', { replace: true });
        };
        const expired = () => {
            // Suppression de la connexion et retour à la page de connexion
            // Avec un message informant la personne
            setUser(null);
            navigate('/');
            showMessage?.infoAlert('Votre session a expiré. Merci de vous reconnecter.');
        };
        return { user, signin, signout, expired, loggedInAs, signoutLoggedInAs };
    }, [user, setUser, navigate, showMessage]);

    // Vérifie le token
    AuthVerify(value);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
